import { useState } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Card,
  CardContent,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  TextField,
  Tabs,
  Tab,
  Collapse,
  IconButton,
  Link,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import HistoryIcon from "@mui/icons-material/History";
import PaidIcon from "@mui/icons-material/Paid";
import { useAuthStore } from "../../stores/root_store";
import { taskPaymentApi } from "../../api/task_payment_api";
import type { TaskPaymentResult } from "../../api/task_payment_api";

type PaymentMode = "run" | "missing" | "mentor-only";

// Format date as YYYY-MM-DD for input
function formatDateForInput(date: Date): string {
  return date.toISOString().split("T")[0];
}

// Get date 15 days ago
function getDefaultStartDate(): string {
  const date = new Date();
  date.setDate(date.getDate() - 15);
  return formatDateForInput(date);
}

// Get today's date
function getDefaultEndDate(): string {
  return formatDateForInput(new Date());
}

export const TaskPaymentPage = observer(() => {
  const navigate = useNavigate();
  const authStore = useAuthStore();

  const [mode, setMode] = useState<PaymentMode>("run");
  const [dateStart, setDateStart] = useState(getDefaultStartDate());
  const [dateEnd, setDateEnd] = useState(getDefaultEndDate());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<TaskPaymentResult | null>(null);
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());

  const hasSudo = authStore.roles.includes("sudo");

  const toggleUserExpanded = (userKey: string) => {
    setExpandedUsers((prev) => {
      const next = new Set(prev);
      if (next.has(userKey)) {
        next.delete(userKey);
      } else {
        next.add(userKey);
      }
      return next;
    });
  };

  const handleModeChange = (_: React.SyntheticEvent, newMode: PaymentMode) => {
    setMode(newMode);
    setResult(null);
    setError(null);
  };

  const handlePreview = async () => {
    if (!dateStart || !dateEnd) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const apiCall =
        mode === "run"
          ? taskPaymentApi.runPayment
          : mode === "mentor-only"
            ? taskPaymentApi.mentorOnly
            : taskPaymentApi.findMissing;
      const data = await apiCall(dateStart, dateEnd, true);
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to preview");
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    if (!dateStart || !dateEnd || !result) return;
    const confirmMessage =
      mode === "run"
        ? "Are you sure? This will mark all requests as paid and create payment records. This cannot be undone."
        : mode === "mentor-only"
          ? "Are you sure? This will mark mentor claims as paid and create payment records. This cannot be undone."
          : "Are you sure? This will create FHAdminPayment records for the missing tasks. This cannot be undone.";
    if (!window.confirm(confirmMessage)) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const apiCall =
        mode === "run"
          ? taskPaymentApi.runPayment
          : mode === "mentor-only"
            ? taskPaymentApi.mentorOnly
            : taskPaymentApi.findMissing;
      const data = await apiCall(dateStart, dateEnd, false);
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to execute");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCSV = () => {
    if (!result?.csv) return;
    const blob = new Blob([result.csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `task-payment-${dateStart}-to-${dateEnd}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!authStore.initialized) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!hasSudo) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          You must have sudo access to view this page.
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/assistant")}
          sx={{ mt: 2 }}
        >
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/manager")}
        >
          Back to Manager
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<PaidIcon />}
            onClick={() => navigate("/manager/task-payment/paid")}
          >
            Paid Tasks
          </Button>
          <Button
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={() => navigate("/manager/task-payment/history")}
          >
            View Batch History
          </Button>
        </Box>
      </Box>

      <Typography variant="h4" gutterBottom>
        Task Payment
      </Typography>

      <Tabs value={mode} onChange={handleModeChange} sx={{ mb: 3 }}>
        <Tab value="run" label="Pay Unpaid Tasks" />
        <Tab value="missing" label="Find Missing Payments" />
        <Tab value="mentor-only" label="Pay Mentor Claims Only" />
      </Tabs>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {mode === "run"
          ? "Pay assistants for completed tasks that are not yet marked as paid. This runs in dry-run mode by default."
          : mode === "mentor-only"
            ? "Pay only mentor claims for tasks in the date range, regardless of task payment status. Use this to pay mentor claims missed in a previous payment run."
            : "Find tasks that were marked as paid but are missing from FHAdminPayment records. Use this to fix payment gaps."}
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Date Range{" "}
            {mode === "missing" && "(by date_solved)"}
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <TextField
              label="Start Date"
              type="date"
              value={dateStart}
              onChange={(e) => setDateStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              label="End Date"
              type="date"
              value={dateEnd}
              onChange={(e) => setDateEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <Button
              variant="contained"
              onClick={handlePreview}
              disabled={loading || !dateStart || !dateEnd}
            >
              {loading ? <CircularProgress size={20} /> : "Preview"}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {result && (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="h6">
                    {result.dry_run ? "Preview Results" : "Payment Complete"}
                  </Typography>
                  {result.dry_run && (
                    <Chip label="DRY RUN" color="warning" size="small" />
                  )}
                  {!result.dry_run && (
                    <Chip label="EXECUTED" color="success" size="small" />
                  )}
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadCSV}
                    disabled={!result.csv}
                  >
                    Download CSV
                  </Button>
                  {result.dry_run && (
                    <Button
                      variant="contained"
                      color="error"
                      onClick={handleExecute}
                      disabled={loading || result.user_count === 0}
                    >
                      {mode === "run"
                        ? "Run Payment"
                        : mode === "mentor-only"
                          ? "Pay Mentor Claims"
                          : "Create Records"}
                    </Button>
                  )}
                </Box>
              </Box>

              <Box sx={{ display: "flex", gap: 4, mb: 2, flexWrap: "wrap" }}>
                <Typography>
                  <strong>Total Amount:</strong> $
                  {result.total_amount.toFixed(2)}
                </Typography>
                <Typography>
                  <strong>Users:</strong> {result.user_count}
                </Typography>
                <Typography>
                  <strong>
                    {mode === "mentor-only" ? "Mentor Claims:" : "Requests:"}
                  </strong>{" "}
                  {mode === "mentor-only"
                    ? result.claim_count
                    : result.request_count}
                </Typography>
              </Box>

              {result.run_id && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Run ID:</strong> <code>{result.run_id}</code>
                  </Typography>
                  {result.batch_uuid && !result.dry_run && (
                    <Button
                      size="small"
                      onClick={() =>
                        navigate(
                          `/manager/task-payment/history/${result.run_id}`
                        )
                      }
                      sx={{ mt: 1 }}
                    >
                      View Batch Details
                    </Button>
                  )}
                </Box>
              )}

              {result.errors && result.errors.length > 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {result.errors.length} error(s) occurred during execution:
                  </Typography>
                  {result.errors.map((err, i) => (
                    <Typography key={i} variant="body2">
                      Phase: {err.phase}
                      {err.user_key && ` | User: ${err.user_key}`}
                      {err.batch_start !== undefined &&
                        ` | Batch: ${err.batch_start}-${(err.batch_start ?? 0) + (err.batch_size ?? 0)}`}
                      {" | "}
                      {err.error}
                    </Typography>
                  ))}
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" gutterBottom>
                Payment Summary
              </Typography>

              {result.summary.length === 0 ? (
                <Alert severity="info">
                  No payments to process for the selected date range.
                </Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {mode === "missing" && <TableCell />}
                        <TableCell>Email</TableCell>
                        <TableCell>Name</TableCell>
                        {mode === "mentor-only" ? (
                          <>
                            <TableCell align="right">Mentor Claims</TableCell>
                            <TableCell align="right">Mentor $</TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell align="right">Tasks</TableCell>
                            <TableCell align="right">Task $</TableCell>
                            {mode === "run" && (
                              <>
                                <TableCell align="right">Mentor Claims</TableCell>
                                <TableCell align="right">Mentor $</TableCell>
                              </>
                            )}
                          </>
                        )}
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.summary.map((user) => (
                        <>
                          <TableRow key={user.user_key}>
                            {mode === "missing" && (
                              <TableCell>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    toggleUserExpanded(user.user_key)
                                  }
                                >
                                  {expandedUsers.has(user.user_key) ? (
                                    <KeyboardArrowUpIcon />
                                  ) : (
                                    <KeyboardArrowDownIcon />
                                  )}
                                </IconButton>
                              </TableCell>
                            )}
                            <TableCell>{user.email}</TableCell>
                            <TableCell>{user.name}</TableCell>
                            {mode === "mentor-only" ? (
                              <>
                                <TableCell align="right">
                                  {user.mentor_claim_count ?? 0}
                                </TableCell>
                                <TableCell align="right">
                                  ${(user.mentor_total ?? 0).toFixed(2)}
                                </TableCell>
                              </>
                            ) : (
                              <>
                                <TableCell align="right">
                                  {user.task_count}
                                </TableCell>
                                <TableCell align="right">
                                  ${user.task_total.toFixed(2)}
                                </TableCell>
                                {mode === "run" && (
                                  <>
                                    <TableCell align="right">
                                      {user.mentor_claim_count ?? 0}
                                    </TableCell>
                                    <TableCell align="right">
                                      ${(user.mentor_total ?? 0).toFixed(2)}
                                    </TableCell>
                                  </>
                                )}
                              </>
                            )}
                            <TableCell align="right">
                              <strong>${user.total.toFixed(2)}</strong>
                            </TableCell>
                          </TableRow>
                          {mode === "missing" && (
                            <TableRow key={`${user.user_key}-detail`}>
                              <TableCell colSpan={6} sx={{ py: 0 }}>
                                <Collapse
                                  in={expandedUsers.has(user.user_key)}
                                  timeout="auto"
                                  unmountOnExit
                                >
                                  <Box sx={{ m: 2 }}>
                                    {user.debug && (
                                      <Box
                                        sx={{
                                          mb: 2,
                                          p: 1,
                                          bgcolor: "action.hover",
                                          borderRadius: 1,
                                        }}
                                      >
                                        <Typography
                                          variant="subtitle2"
                                          gutterBottom
                                        >
                                          Debug Info
                                        </Typography>
                                        <Typography variant="body2">
                                          Paid tasks in date range:{" "}
                                          {user.debug.total_paid_tasks_in_range}{" "}
                                          | FHAdminPayment records checked:{" "}
                                          {user.debug.payment_records_checked} |
                                          Records with requests:{" "}
                                          {
                                            user.debug
                                              .payment_records_with_requests
                                          }{" "}
                                          | Total request keys found:{" "}
                                          {
                                            user.debug
                                              .total_request_keys_in_payments
                                          }
                                        </Typography>
                                        {user.debug.recent_payments.length >
                                          0 && (
                                          <Box sx={{ mt: 1 }}>
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                            >
                                              Recent FHAdminPayment records:
                                            </Typography>
                                            {user.debug.recent_payments.map(
                                              (p, i) => (
                                                <Typography
                                                  key={i}
                                                  variant="caption"
                                                  display="block"
                                                  sx={{ ml: 1 }}
                                                >
                                                  {p.name} - $
                                                  {p.base_pay?.toFixed(2)} -{" "}
                                                  {p.request_count} requests -{" "}
                                                  {p.date}
                                                </Typography>
                                              ),
                                            )}
                                          </Box>
                                        )}
                                      </Box>
                                    )}
                                    <Typography
                                      variant="subtitle2"
                                      gutterBottom
                                    >
                                      Missing Tasks (
                                      {user.missing_tasks?.length ?? 0})
                                    </Typography>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Request UUID</TableCell>
                                          <TableCell>Date Solved</TableCell>
                                          <TableCell>
                                            Date Marked Paid
                                          </TableCell>
                                          <TableCell align="right">
                                            Price
                                          </TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {user.missing_tasks?.map((task) => {
                                          const requestId =
                                            task.request_uuid ||
                                            task.request_key;
                                          const displayId =
                                            task.request_uuid ||
                                            `${task.request_key.slice(0, 20)}...`;
                                          return (
                                            <TableRow key={task.request_key}>
                                              <TableCell>
                                                <Link
                                                  href={`/assistant/request/${requestId}`}
                                                  target="_blank"
                                                >
                                                  {displayId}
                                                </Link>
                                              </TableCell>
                                              <TableCell>
                                                {task.date_solved?.split(
                                                  "T",
                                                )[0] ?? "-"}
                                              </TableCell>
                                              <TableCell>
                                                {task.solver_paid_date?.split(
                                                  "T",
                                                )[0] ?? "-"}
                                              </TableCell>
                                              <TableCell align="right">
                                                ${task.price.toFixed(2)}
                                              </TableCell>
                                            </TableRow>
                                          );
                                        })}
                                      </TableBody>
                                    </Table>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          )}
                        </>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {result.skipped_users.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" color="warning.main" gutterBottom>
                  Skipped Users ({result.skipped_users.length})
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  These users had completed tasks but could not be paid for the
                  following reasons:
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Email</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.skipped_users.map((user) => (
                        <TableRow key={user.user_key}>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>{user.name}</TableCell>
                          <TableCell>{user.reason}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Container>
  );
});
